import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Plus, 
  Search, 
  Filter, 
  Star,
  Clock,
  Users,
  Camera,
  Upload,
  Heart,
  ChefHat,
  Scan,
  FileText,
  Award,
  Globe,
  User,
  Download,
  Eye,
  Edit,
  Trash2,
  Tag,
  Utensils,
  DollarSign,
  Gift,
  TrendingUp,
  MessageCircle,
  Share2,
  Bookmark,
  UserPlus,
  Crown,
  Coins,
  ThumbsUp,
  Flag,
  Settings,
  Bell,
  Coffee,
  X,
  Check,
  AlertCircle,
  Zap,
  Trophy,
  Brain,
  Sparkles
} from 'lucide-react';
import { localUsdaApi } from '../../lib/localUsdaApi';
import { barcodeApi } from '../../lib/barcodeApi';
import SocialRecipeCard from './SocialRecipeCard';
import CommunityLeaderboardComponent from './CommunityLeaderboard';
import { useExampleData } from '../../data/example/ExampleDataProvider';
import { MockBadge } from '../common/MockBadge';
import { DataDisclaimer } from '../common/DataDisclaimer';
import { 
  RecipeContributor, 
  RecipeContribution, 
  RecipeTip,
  RecipeAnalytics,
  RecipeInteraction,
  RecipeEngagement,
  ZippyAIAnalysisRequest
} from '../../types/users';
import { getComponentClass } from '../../styles/theme';

interface Recipe {
  id: string;
  name: string;
  description: string;
  servings: number;
  prep_time: number;
  cook_time: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  cuisine_type: string;
  dietary_tags: string[];
  ingredients: RecipeIngredient[];
  instructions: string[];
  nutrition_per_serving: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    sugar: number;
    sodium: number;
  };
  source: 'guru' | 'personal' | 'public_domain' | 'community' | 'scanned';
  source_details: {
    guru_name?: string;
    guru_specialty?: string;
    book_title?: string;
    book_author?: string;
    publication_year?: number;
    contributed_by?: string;
    scan_source?: string;
    original_text?: string;
  };
  image_url?: string;
  rating: number;
  save_count: number;
  is_favorited: boolean;
  is_private: boolean;
  tags: string[];
  created_at: string;
  updated_at: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  
  // Community features
  contributor?: RecipeContributor;
  contribution?: RecipeContribution;
  engagement?: RecipeEngagement;
  userCanTip?: boolean;
  aiAnalysis?: any; // Add this for the mock data
}

interface RecipeIngredient {
  id: string;
  name: string;
  amount: number;
  unit: string;
  fdc_id?: number; // USDA database link
  ndc_code?: string; // For supplements
  notes?: string;
  substitutions?: string[];
  is_optional?: boolean;
  preparation?: string; // 'diced', 'chopped', 'minced', etc.
}

interface RecipeCollection {
  id: string;
  name: string;
  description: string;
  type: 'guru' | 'public_domain' | 'personal' | 'community';
  recipe_count: number;
  author?: string;
  publication_info?: {
    title: string;
    author: string;
    year: number;
    copyright_status: 'public_domain' | 'expired' | 'fair_use';
  };
  guru_info?: {
    name: string;
    specialty: string;
    credentials: string[];
    verified: boolean;
  };
  cover_image?: string;
  created_at: string;
}

const RecipeBook: React.FC = () => {
  const [activeView, setActiveView] = useState<'browse' | 'contribute' | 'my-recipes' | 'leaderboard'>('browse');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [myContributions, setMyContributions] = useState<RecipeContribution[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showContributeModal, setShowContributeModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [userProfile, setUserProfile] = useState<RecipeContributor | null>(null);
  const [userInteractions, setUserInteractions] = useState<Map<string, RecipeInteraction>>(new Map());
  const [recipeAnalytics, setRecipeAnalytics] = useState<Map<string, RecipeAnalytics>>(new Map());

  const { getRecipesNew, getContributorsNew, shouldShowExampleData } = useExampleData();
  const isExampleData = shouldShowExampleData('recipe-book');

  useEffect(() => {
    loadRecipes();
    loadUserProfile();
    loadUserInteractions();
    loadRecipeAnalytics();
  }, [isExampleData]);

  const loadRecipes = async () => {
    setIsLoading(true);
    try {
      if (isExampleData) {
        const exampleRecipes = getRecipesNew();
        setRecipes(exampleRecipes as Recipe[]);
      } else {
        // Load from real API
        // const apiRecipes = await fetchRecipesFromAPI();
        // setRecipes(apiRecipes);
        setRecipes([]);
      }
    } catch (error) {
      console.error('Error loading recipes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserProfile = () => {
    if (isExampleData) {
      const exampleContributors = getContributorsNew();
      if (exampleContributors.length > 0) {
        // Use the first contributor as the current user profile
        const firstContributor = exampleContributors[0];
        setUserProfile({
          id: firstContributor.id,
          userId: 'current-user',
          displayName: firstContributor.name,
          avatar: firstContributor.avatar,
          bio: firstContributor.bio,
          location: 'Your Location',
          totalRecipesShared: firstContributor.recipe_count,
          totalTipsReceived: firstContributor.follower_count,
          totalTipsGiven: 23,
          reputation: Math.round(firstContributor.rating * 20),
          verificationStatus: firstContributor.verified ? 'verified' : 'unverified',
          level: Math.floor(firstContributor.rating),
          specialties: [firstContributor.specialty],
          joinedDate: '2023-01-15',
          followers: firstContributor.follower_count,
          following: 89
        });
      }
    } else {
      // Load from real API
      // const apiProfile = await fetchUserProfileFromAPI();
      // setUserProfile(apiProfile);
      setUserProfile(null);
    }
  };

  const loadUserInteractions = () => {
    // Mock user interactions
    const interactions = new Map<string, RecipeInteraction>();
    interactions.set('recipe-1', {
      id: 'int-1',
      userId: 'current-user',
      recipeId: 'recipe-1',
      contributionId: 'contrib-1',
      liked: true,
      saved: true,
      downloaded: false,
      shared: false,
      viewed: true,
      cooked: true,
      likedAt: new Date().toISOString(),
      savedAt: new Date().toISOString(),
      viewedAt: new Date().toISOString(),
      cookedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    setUserInteractions(interactions);
  };

  const loadRecipeAnalytics = () => {
    // Mock analytics data
    const analytics = new Map<string, RecipeAnalytics>();
    analytics.set('recipe-1', {
      id: 'analytics-1',
      recipeId: 'recipe-1',
      contributionId: 'contrib-1',
      totalViews: 12847,
      uniqueViewers: 8934,
      totalLikes: 2341,
      totalSaves: 1876,
      totalDownloads: 934,
      totalShares: 445,
      totalCooks: 567,
      averageRating: 4.8,
      totalReviews: 234,
      reviewsWithPhotos: 89,
      helpfulReviewsCount: 178,
      viewsLast24h: 234,
      viewsLast7d: 1567,
      viewsLast30d: 5678,
      trendingScore: 87,
      totalTipsReceived: 450,
      totalZippyCoinsSpentOnAnalysis: 150,
      communityDataContributions: 23,
      popularWithDemographics: ['families', 'baking_enthusiasts'],
      geographicPopularity: ['Midwest', 'Northeast'],
      seasonalPopularity: { fall: 85, winter: 70, spring: 45, summer: 30 },
      lastUpdated: new Date().toISOString()
    });
    setRecipeAnalytics(analytics);
  };

  // Social interaction handlers
  const handleLike = (recipeId: string) => {
    const interaction = userInteractions.get(recipeId);
    if (interaction) {
      const updated = { ...interaction, liked: !interaction.liked };
      setUserInteractions(new Map(userInteractions.set(recipeId, updated)));
    }
  };

  const handleSave = (recipeId: string) => {
    const interaction = userInteractions.get(recipeId);
    if (interaction) {
      const updated = { ...interaction, saved: !interaction.saved };
      setUserInteractions(new Map(userInteractions.set(recipeId, updated)));
    }
  };

  const handleDownload = (recipeId: string) => {
    console.log('Downloading recipe:', recipeId);
    // Implement download functionality
  };

  const handleShare = (recipeId: string) => {
    console.log('Sharing recipe:', recipeId);
    // Implement share functionality
  };

  const handleView = (recipeId: string) => {
    console.log('Viewing recipe details:', recipeId);
    // Implement detailed view
  };

  const handleTip = (recipeId: string) => {
    console.log('Tipping contributor for recipe:', recipeId);
    // Implement tipping functionality
  };

  const handleRequestAIAnalysis = (recipeId: string, analysisType: string) => {
    console.log('Requesting AI analysis for recipe:', recipeId, 'Type:', analysisType);
    // Implementation would integrate with AI analysis system
  };

  const handleMarkAsCooked = (recipeId: string) => {
    const interaction = userInteractions.get(recipeId);
    if (interaction) {
      const updated = { ...interaction, cooked: !interaction.cooked };
      setUserInteractions(prev => new Map(prev.set(recipeId, updated)));
    }
  };

  // Filter and search functionality
  const getFilteredRecipes = () => {
    let filtered = recipes;

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(recipe =>
        recipe.name.toLowerCase().includes(query) ||
        recipe.description.toLowerCase().includes(query) ||
        recipe.cuisine_type.toLowerCase().includes(query) ||
        recipe.dietary_tags.some(tag => tag.toLowerCase().includes(query)) ||
        recipe.ingredients.some(ing => ing.name.toLowerCase().includes(query))
      );
    }

    // Apply filters
    if (selectedFilters.length > 0) {
      filtered = filtered.filter(recipe => {
        return selectedFilters.every(filter => {
          // Dietary filters
          if (['vegetarian', 'vegan', 'gluten-free', 'dairy-free'].includes(filter)) {
            return recipe.dietary_tags.includes(filter);
          }
          
          // Difficulty filters
          if (['easy', 'intermediate', 'advanced'].includes(filter)) {
            return recipe.difficulty.toLowerCase() === filter;
          }
          
          // Cuisine filters
          if (['american', 'italian', 'mexican', 'asian', 'mediterranean'].includes(filter)) {
            return recipe.cuisine_type.toLowerCase() === filter;
          }
          
          // Time filters
          if (filter === 'quick') {
            return (recipe.prep_time + recipe.cook_time) <= 30;
          }
          
          if (filter === 'slow') {
            return (recipe.prep_time + recipe.cook_time) > 60;
          }

          return false;
        });
      });
    }

    return filtered;
  };

  const handleFilterToggle = (filter: string) => {
    setSelectedFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const clearFilters = () => {
    setSelectedFilters([]);
    setSearchQuery('');
  };

  const availableFilters = [
    { id: 'vegetarian', label: 'Vegetarian', category: 'dietary' },
    { id: 'vegan', label: 'Vegan', category: 'dietary' },
    { id: 'gluten-free', label: 'Gluten-Free', category: 'dietary' },
    { id: 'dairy-free', label: 'Dairy-Free', category: 'dietary' },
    { id: 'easy', label: 'Easy', category: 'difficulty' },
    { id: 'intermediate', label: 'Intermediate', category: 'difficulty' },
    { id: 'advanced', label: 'Advanced', category: 'difficulty' },
    { id: 'quick', label: 'Quick (≤30 min)', category: 'time' },
    { id: 'slow', label: 'Slow Cooking', category: 'time' },
    { id: 'american', label: 'American', category: 'cuisine' },
    { id: 'italian', label: 'Italian', category: 'cuisine' },
    { id: 'mexican', label: 'Mexican', category: 'cuisine' },
    { id: 'asian', label: 'Asian', category: 'cuisine' },
    { id: 'mediterranean', label: 'Mediterranean', category: 'cuisine' }
  ];

  const filteredRecipes = getFilteredRecipes();

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Example Data Disclaimer */}
      {isExampleData && (
        <DataDisclaimer 
          type="info"
          title="Example Recipe Book"
          message="These are example recipes and contributors for demonstration. Connect with real chefs and nutritionists when you're ready to share your own recipes."
          className="mb-6"
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BookOpen className="w-8 h-8 mr-3 text-blue-600" />
            ZippyVerse Recipe Book
            {isExampleData && <MockBadge variant="small" className="ml-3" />}
          </h1>
          <p className="text-gray-600 mt-2">
            A community-driven collection of recipes, reviews, and culinary wisdom
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowContributeModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Contribute Recipe
          </button>
          
          {userProfile && (
            <div className="flex items-center space-x-2 bg-gray-100 px-3 py-2 rounded-lg">
              <Coins className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium">1,247 ZippyCoins</span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
        {[
          { id: 'browse', label: 'Browse Recipes', icon: Search },
          { id: 'leaderboard', label: 'Community Leaders', icon: Trophy },
          { id: 'my-recipes', label: 'My Contributions', icon: User },
          { id: 'contribute', label: 'Add Recipe', icon: Plus }
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id as any)}
              className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeView === tab.id
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content based on active view */}
      {activeView === 'browse' && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Search and Filters */}
            <div className={`${getComponentClass('card')} p-6 mb-6`}>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search recipes, ingredients, or contributors..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <button
                    onClick={clearFilters}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Clear Filters ({selectedFilters.length})
                  </button>
                </div>
              </div>
              
              {/* Active Filters */}
              {selectedFilters.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mt-4">
                  <span className="text-sm text-gray-600">Active filters:</span>
                  {selectedFilters.map(filter => (
                    <span key={filter} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs flex items-center gap-1">
                      {availableFilters.find(f => f.id === filter)?.label}
                      <button onClick={() => handleFilterToggle(filter)} className="text-blue-600 hover:text-blue-800">×</button>
                    </span>
                  ))}
                </div>
              )}
              
              {/* Filter Categories */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                {['dietary', 'difficulty', 'time', 'cuisine'].map(category => (
                  <div key={category}>
                    <div className="text-sm font-medium text-gray-700 mb-2 capitalize">{category}</div>
                    <div className="space-y-1">
                      {availableFilters
                        .filter(filter => filter.category === category)
                        .map(filter => (
                          <button
                            key={filter.id}
                            onClick={() => handleFilterToggle(filter.id)}
                            className={`w-full text-left px-2 py-1 rounded text-sm transition-colors ${
                              selectedFilters.includes(filter.id)
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {filter.label}
                          </button>
                        ))
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recipe Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredRecipes.map(recipe => (
                <SocialRecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  userInteraction={userInteractions.get(recipe.id)}
                  analytics={recipeAnalytics.get(recipe.id)}
                  onLike={handleLike}
                  onSave={handleSave}
                  onDownload={handleDownload}
                  onShare={handleShare}
                  onView={handleView}
                  onTip={handleTip}
                  onRequestAIAnalysis={handleRequestAIAnalysis}
                  onMarkAsCooked={handleMarkAsCooked}
                />
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Community Stats */}
            <div className={`${getComponentClass('card')} p-6`}>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
                Community Impact
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Recipes</span>
                  <span className="font-medium">12,847</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">AI Analyses</span>
                  <span className="font-medium">2,341</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Community Reviews</span>
                  <span className="font-medium">8,934</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Recipes Cooked</span>
                  <span className="font-medium">45,678</span>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-2">
                  <Brain className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-gray-600">ZippyCoins Invested</span>
                </div>
                <div className="text-2xl font-bold text-purple-600 mt-1">156,789</div>
                <div className="text-xs text-gray-500">Building our shared knowledge</div>
              </div>
            </div>

            {/* Trending Tags */}
            <div className={`${getComponentClass('card')} p-6`}>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                Trending Now
              </h3>
              
              <div className="flex flex-wrap gap-2">
                {['Quick Meals', 'Heart Healthy', 'Comfort Food', 'Seasonal', 'AI Analyzed', 'Family Favorite'].map(tag => (
                  <span key={tag} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeView === 'leaderboard' && (
        <CommunityLeaderboardComponent />
      )}

      {activeView === 'my-recipes' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">My Recipe Contributions</h2>
          <p className="text-gray-600">Your contributed recipes will appear here.</p>
        </div>
      )}

      {activeView === 'contribute' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Contribute a Recipe</h2>
          <p className="text-gray-600">Recipe contribution form will be implemented here.</p>
        </div>
      )}
    </div>
  );
};

export default RecipeBook; 
